import { useEffect, useRef } from "react";
import { FireServices } from "../../../../../shared/services/api/fire/FireServices";

interface UseFireAssistancesLoaderParams {
	clientId: string | null;
	propertyType: string;
	rawGroups: any[];
	TOKIO_ID: string;
	setAssistancesMap: (v: { value: string; label: string }[]) => void;
	setAssistanceId?: (v: string | null) => void;
	mode: "create" | "edit";
	formRef: any;
}

export function useFireAssistancesLoader(params: UseFireAssistancesLoaderParams) {
	const { clientId, propertyType, rawGroups, TOKIO_ID, setAssistancesMap, setAssistanceId, mode, formRef } = params;

	// controla a primeira execução no modo edição
	const isInitialLoadRef = useRef(true);

	useEffect(() => {
		// sem cliente ou propertyType → não faz nada
		if (!propertyType || !clientId) return;

		// depende dos grupos já carregados pelo useFireQuotationLoader
		if (!Array.isArray(rawGroups) || rawGroups.length === 0) return;

		const selectedGroup = rawGroups.find((g) => g.id === propertyType);
		const category = selectedGroup?.category;
		if (!category) return;

		const loadAssistances = async () => {
			const result = await FireServices.getAllAssistanceByOccupation(clientId, TOKIO_ID, category);

			if (!Array.isArray(result)) return;

			const mapped = result.map((item: any) => ({
				id: String(item.id),
				description: item.description ?? "",
				ass_default: item.ass_default ?? false,
			}));

			// Atualiza lista para o select
			setAssistancesMap(
				mapped.map((a) => ({
					value: a.id,
					label: a.description,
				}))
			);

			const defaultAssist = mapped.find((a) => a.ass_default);

			// valor atual no form (pode vir do loader de edição)
			const formData = formRef.current?.getData?.() || {};
			const currentAssistFromForm = formData.assistanceId as string | undefined;

			// ============================================================
			// MODO EDIT → PRIMEIRA EXECUÇÃO
			// - Não mexe na assistência (mantém a vinda do backend)
			// ============================================================
			if (mode === "edit" && isInitialLoadRef.current) {
				isInitialLoadRef.current = false;
				return;
			}

			// ============================================================
			// MODO CREATE → sempre tentar usar a default, se existir
			// MODO EDIT (depois da primeira vez) → ao trocar propertyType,
			// também usar a default, se existir
			// ============================================================
			if (defaultAssist) {
				// atualiza estado externo, se tiver
				if (setAssistanceId) {
					setAssistanceId(defaultAssist.id);
				}

				// sincroniza o form (campo <Select name="assistanceId" />)
				formRef.current?.setFieldValue?.("assistanceId", defaultAssist.id);
				return;
			}

			// Se não tiver default e o form já possui um valor (edição),
			// não fazemos nada: mantemos o que já está no form.
			if (currentAssistFromForm) {
				return;
			}
		};

		loadAssistances();
	}, [clientId, propertyType, rawGroups]);
}
